/**
 * SCR-009 — Course detail with curriculum builder tabs.
 * Tabs: Tổng quan | Units & Bài học | Bài tập tổng.
 * Tab state synced to ?tab= query param.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { CourseDetailTabs } from '@client/components/curriculum/course-detail-tabs';
import { useCourse } from '@client/lib/queries/use-courses';

export const Route = createFileRoute('/admin/courses/$courseId')({
  component: CourseDetailPage,
});

const CEFR_LABELS: Record<string, string> = {
  pre_a1: 'Pre A1', a1: 'A1', a2: 'A2',
  b1: 'B1', b2: 'B2', c1: 'C1', c2: 'C2',
};

function CourseDetailPage() {
  const { courseId } = Route.useParams();
  const { data: course, isLoading, isError } = useCourse(courseId);

  if (isLoading) return <CourseDetailSkeleton />;

  if (isError || !course) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Không tải được thông tin khóa học.{' '}
          <Link to="/admin/courses" className="underline">Quay lại</Link>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/courses">Khóa học</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{course.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-foreground">{course.name}</h1>
        <Badge className="bg-brand-green-100 text-brand-green-700 border-0">
          {CEFR_LABELS[course.level] ?? course.level}
        </Badge>
        {course.isActive ? (
          <Badge className="bg-brand-green-100 text-brand-green-700 border-0 text-xs">Đang mở</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">Ngừng</Badge>
        )}
        <span className="text-sm text-muted-foreground font-mono">{course.code}</span>
      </div>

      {/* Tabs */}
      <CourseDetailTabs courseId={courseId} />
    </div>
  );
}

function CourseDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-8 w-56" />
      <div className="flex flex-col md:flex-row gap-6">
        <Skeleton className="h-24 w-full md:w-44 shrink-0 rounded-lg" />
        <div className="flex-1 flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}
